<template>
    <!-- Weight detail modal: the figures behind a section's Weight / Daily Gain / Growth Index
         cells. Three headline cards carry the latest value for each metric, a Growth Index chart
         shows the index over time, and a combined Weight + Daily Gain chart overlays both series on
         a dual y-axis and carries the one navigator both charts range off. Shell (teleport/overlay/
         header/prev-next/Escape+Arrow keys) lives in DetailModalShell, shared with
         WaterDetailModal/MortalityDetailModal. Opens from three different cells (Weight, Daily Gain,
         Growth Index) whenever `section.hasWeightSensor` is true — each headline card handles its
         own null state.
         A Section measured by more than one camera can draw each camera's own curve alongside the
         aggregate on both charts, and offers a curve picker over them; the cameras start hidden,
         since the Section figure is what the modal is opened to read. A single-camera Section draws
         the aggregate alone, since there the aggregate is that one camera, and keeps its legend as
         the control. -->
    <DetailModalShell :section="section" :sections="sections" @close="emit('close')" @navigate="onNavigate" @handle-alarm="onHandleAlarm">
        <template #default="{ section: activeSection }">
            <!-- Headline: three big-number cards, one per metric, each handling its own null
                 state independently (this modal opens from any of the three cells). -->
            <div class="grid grid-cols-3 gap-3">
                <div class="rounded-lg bg-white p-4 shadow-sm">
                    <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.weightCard') }}
                    </h3>
                    <p v-if="activeSection.latestWeightKg !== null" class="mt-2 flex items-baseline gap-1">
                        <span class="text-[28px] font-extrabold leading-tight tracking-tight tabular-nums" :class="activeSection.weightSource === 'Estimated' ? 'text-[#8C96A3]' : 'text-gray-900'">{{
                            $n(activeSection.latestWeightKg, 'weight')
                        }}</span>
                        <small class="text-xs" :class="activeSection.weightSource === 'Estimated' ? 'text-[#B6BEC9]' : 'text-gray-400'">
                            {{ activeSection.weightSource === 'Estimated' ? 'kg · est.' : 'kg' }}
                        </small>
                        <!-- The figure stands, and says what it rests on: fewer cameras reported
                             within the staleness window than the Section has mounted. Each card reads
                             its OWN count - the three diverge for the same reason the three instants
                             below them do. -->
                        <DegradedCameraMarker :contributing="activeSection.contributingWeightCameraCount" :expected="activeSection.expectedCameraCount" />
                    </p>
                    <!-- Each card states its OWN instant, and the three routinely differ: every
                         figure is the latest known value of that figure, and a camera's newest reading
                         carries a weight far more often than a growth, so the gain and the index trail
                         the weight. Stated at all because the charts below answer "when" by a third
                         rule - a day bucket is read at one fixed hour of the directory's day - so a
                         card and the chart's last point differ by a few hours' growth. Without the
                         instant that difference reads as one of them being wrong. -->
                    <p v-if="activeSection.latestWeightKg !== null && activeSection.weightAsOfUtc" class="mt-1 text-[10px] leading-tight text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.figureAsOf', { time: $d(new Date(activeSection.weightAsOfUtc), 'long') }) }}
                    </p>
                    <p v-if="activeSection.latestWeightKg === null" class="mt-2 flex items-center gap-2">
                        <span class="text-[28px] font-extrabold leading-tight tracking-tight text-gray-300">{{ placeholder }}</span>
                        <span :title="$t('views.monitoring.activeGroups.texts.noData')">
                            <WifiOff class="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
                        </span>
                    </p>
                </div>

                <div class="rounded-lg bg-white p-4 shadow-sm">
                    <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.dailyGainCard') }}
                    </h3>
                    <p v-if="activeSection.dailyGain !== null" class="mt-2 flex items-baseline gap-1">
                        <span class="text-[28px] font-extrabold leading-tight tracking-tight tabular-nums text-gray-900">{{ $n(activeSection.dailyGain, 'decimal') }}</span>
                        <small class="text-xs text-gray-400">{{ $t('views.monitoring.activeGroups.texts.dailyGainUnit') }}</small>
                        <DegradedCameraMarker :contributing="activeSection.contributingDailyGainCameraCount" :expected="activeSection.expectedCameraCount" />
                    </p>
                    <!-- Its own instant, not the weight's - see the weight card. -->
                    <p v-if="activeSection.dailyGain !== null && activeSection.dailyGainAsOfUtc" class="mt-1 text-[10px] leading-tight text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.figureAsOf', { time: $d(new Date(activeSection.dailyGainAsOfUtc), 'long') }) }}
                    </p>
                    <p v-if="activeSection.dailyGain === null" class="mt-2 flex items-center gap-2">
                        <span class="text-[28px] font-extrabold leading-tight tracking-tight text-gray-300">{{ placeholder }}</span>
                        <span :title="$t('views.monitoring.activeGroups.texts.noData')">
                            <WifiOff class="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
                        </span>
                    </p>
                </div>

                <div class="rounded-lg bg-white p-4 shadow-sm">
                    <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.growthIndexCard') }}
                    </h3>
                    <p v-if="activeSection.liveGrowthIndex !== null" class="mt-2 flex items-baseline gap-1">
                        <span class="text-[28px] font-extrabold leading-tight tracking-tight tabular-nums" :class="indexColourClass(activeSection.liveGrowthIndex)">
                            {{ activeSection.liveGrowthIndex }}
                        </span>
                        <!-- The index needs a weight and a growth on one reading, so it rests on the
                             fewest cameras of the three. Its own count, like the other two cards. -->
                        <DegradedCameraMarker :contributing="activeSection.contributingGrowthIndexCameraCount" :expected="activeSection.expectedCameraCount" />
                    </p>
                    <!-- Its own instant, not the weight's - see the weight card. -->
                    <p v-if="activeSection.liveGrowthIndex !== null && activeSection.growthIndexAsOfUtc" class="mt-1 text-[10px] leading-tight text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.figureAsOf', { time: $d(new Date(activeSection.growthIndexAsOfUtc), 'long') }) }}
                    </p>
                    <p v-if="activeSection.liveGrowthIndex === null" class="mt-2 flex items-center gap-2">
                        <span class="text-[28px] font-extrabold leading-tight tracking-tight text-gray-300">{{ placeholder }}</span>
                        <span :title="$t('views.monitoring.activeGroups.texts.noData')">
                            <WifiOff class="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
                        </span>
                    </p>
                </div>
            </div>

            <!-- Growth Index over time: the current Production Group only (its start through today —
                 the backend never returns history beyond the current Production Group, so there's no
                 SP-start boundary to mark). Carries every control the two charts share - the curve
                 picker, the granularity toggle and the reset - because each of them governs both
                 charts, and sitting on the lower chart they would read as local to it. -->
            <div class="rounded-lg bg-white p-4 shadow-sm">
                <div class="mb-2 flex items-center gap-2">
                    <h3 class="flex-1 truncate text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.chartGrowthIndexTitle') }}
                    </h3>
                    <!-- Same amber-glyph signal as the tally chart's negative-balance warning. -->
                    <span v-if="implausibleGrowthIndexCount > 0" class="cursor-help" :title="implausibleGrowthIndexWarning">
                        <AlertTriangle class="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                    </span>
                    <!-- Which curves this modal draws, and the only control over that: the legends
                         below are read-only keys, so one physical camera is ticked once here rather
                         than being chased across two charts and three metric families. A tick means
                         the camera is drawn everywhere the modal plots it. Absent on a single-camera
                         Section, where the aggregate is that camera and there is nothing to pick
                         between. -->
                    <DropdownMenu v-if="multiCamera" :modal="false">
                        <!-- Styled on the trigger itself rather than on an `as-child` button: the
                             wrapper carries its own class, which wins over a child's under
                             `asChild` and silently leaves the button unstyled. -->
                        <DropdownMenuTrigger class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50">
                            <span class="text-gray-400">{{ $t('views.monitoring.activeGroups.modals.weightDetail.curvePickerHeading') }}</span>
                            {{ $t('views.monitoring.activeGroups.modals.weightDetail.curvePickerSummary', { shown: shownCurveCount, total: pickedCurveTotal }) }}
                            <ChevronDown class="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" class="min-w-[12rem]">
                            <!-- `select.prevent` keeps the menu open: the reader is picking a set, and
                                 a menu that closed on each tick would have to be reopened per camera. -->
                            <DropdownMenuCheckboxItem
                                :checked="isCurveShown(AGGREGATE_SERIES_IDS)"
                                indicator-right
                                class="cursor-pointer text-xs"
                                @select.prevent="setCurveShown(AGGREGATE_SERIES_IDS, !isCurveShown(AGGREGATE_SERIES_IDS))"
                            >
                                {{ $t('views.monitoring.activeGroups.modals.weightDetail.curvePickerAggregate') }}
                            </DropdownMenuCheckboxItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuCheckboxItem
                                v-for="entry in cameraPickerEntries"
                                :key="entry.index"
                                :checked="isCurveShown(entry.ids)"
                                indicator-right
                                class="cursor-pointer text-xs"
                                @select.prevent="setCurveShown(entry.ids, !isCurveShown(entry.ids))"
                            >
                                {{ entry.label }}
                            </DropdownMenuCheckboxItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <div class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                        <button
                            v-for="option in granularityOptions"
                            :key="option"
                            type="button"
                            class="rounded-md px-2 py-0.5 text-xs font-medium transition-colors"
                            :class="granularity === option ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100'"
                            @click="granularity = option"
                        >
                            {{ $t(`views.monitoring.activeGroups.texts.${option}`) }}
                        </button>
                    </div>
                    <button
                        type="button"
                        class="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        :title="$t('views.monitoring.activeGroups.modals.mortalityDetail.chartReset')"
                        @click="resetChartView"
                    >
                        <RotateCcw class="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                </div>
                <div v-if="isSeriesLoading" class="flex items-center justify-center py-16">
                    <Spinner class="h-6 w-6" />
                </div>
                <GenericChart
                    v-else-if="hasChartData"
                    :options="(growthIndexChartOptions as any)"
                    :expandable="false"
                    @ready="onChartRendered('growthIndex', $event)"
                    @update="onChartRendered('growthIndex', $event)"
                />
                <p v-else class="py-6 text-center text-xs italic text-gray-400">
                    {{ chartEmptyMessage }}
                </p>
                <!-- Stated, not only hinted at in the glyph's tooltip: the blanked points must not read
                     as missing data. -->
                <p v-if="hasChartData && implausibleGrowthIndexCount > 0" class="mt-1.5 text-[11px] text-amber-700">
                    {{ implausibleGrowthIndexWarning }}
                </p>
                <!-- Grey, not amber: a withheld bucket is the completeness rule working, not a
                     suspect reading. Amber here would put the two on the same footing. -->
                <p v-if="hasChartData && withheldGrowthIndexCount > 0" class="mt-1.5 text-[11px] text-gray-500">
                    {{ withheldGrowthIndexNote }}
                </p>
            </div>

            <!-- Weight + Daily Gain over time: two series on two y-axes overlaid on the same plot
                 area. Carries the one visible navigator, whose drags set the range on both charts;
                 granularity and the curve picker are controlled from the Growth Index chart above.
                 On a single-camera Section, where there is no picker, this chart's legend is still
                 its own visibility control. -->
            <div class="rounded-lg bg-white p-4 shadow-sm">
                <div class="mb-2 flex items-center gap-2">
                    <h3 class="flex-1 truncate text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.weightDetail.chartWeightDailyGainTitle') }}
                    </h3>
                    <span v-if="implausibleDailyGainCount > 0" class="cursor-help" :title="implausibleDailyGainWarning">
                        <AlertTriangle class="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                    </span>
                </div>
                <div v-if="isSeriesLoading" class="flex items-center justify-center py-16">
                    <Spinner class="h-6 w-6" />
                </div>
                <GenericChart v-else-if="hasChartData" :options="(combinedChartOptions as any)" :expandable="false" @ready="onChartRendered('combined', $event)" @update="onChartRendered('combined', $event)" />
                <p v-else class="py-6 text-center text-xs italic text-gray-400">
                    {{ chartEmptyMessage }}
                </p>
                <p v-if="hasChartData && implausibleDailyGainCount > 0" class="mt-1.5 text-[11px] text-amber-700">
                    {{ implausibleDailyGainWarning }}
                </p>
                <!-- See the Growth Index chart above for why this one is grey. -->
                <p v-if="hasChartData && withheldCombinedCount > 0" class="mt-1.5 text-[11px] text-gray-500">
                    {{ withheldCombinedNote }}
                </p>
            </div>
        </template>
    </DetailModalShell>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { AlertTriangle, ChevronDown, WifiOff, RotateCcw } from 'lucide-vue-next';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import { useChartGranularity } from '@/composables/useChartGranularity';
import { useSyncedChartExtremes } from '@/composables/useSyncedChartExtremes';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { useWeightSeries } from '@/plugins/store/actions/queries/active-groups';
import { ChartType } from '@/types';
import type { WeightSeriesDay } from '@/types/SectionLiveKpi';
import { aggregateEmphasis, cameraLabel, cameraSeriesKey, cameraSeriesName, cameraSeriesStyle, drawsCameraSeries, isCameraMetric, type CameraMetric } from './cameraSeries';
import DegradedCameraMarker from './DegradedCameraMarker.vue';
import { directoryLocalDayStart, directoryLocalHourStart } from './directoryLocalNow';
import { indexColourClass } from './indexColour';
import { countImplausible, plottableValue, DAILY_GAIN_G_RANGE, GROWTH_INDEX_RANGE, type PlausibleRange } from './plausibleRange';
import colors from '@/plugins/tailwind';
import { dayInPeriod, periodTickPositions, weekInPeriod } from './periodPosition';
import { CHART_CHROME_COLORS, CHART_SERIES_COLORS } from './productivityColors';
import { DAY_MS, WEEK_MS, productivityDate, productivityInstant } from './productivityFormatting';
import { METRIC_KEYS, metricDecimals } from './productivityMetrics';
import { isolatedPointMarkers } from './productivitySeries';
import type { SectionRow } from './sectionRow';
import { formatActiveGroupsChartTooltipHeader } from './activeGroupsChartFormatting';
import DetailModalShell from './DetailModalShell.vue';

const placeholder = '—';

const props = defineProps<{
    // The row whose Vægt/Tilvækst/Vækstindeks cell was clicked (null = closed).
    section: SectionRow | null;
    // All rows whose Vægt/Tilvækst/Vækstindeks cells can open this modal, in the page's display
    // order — drives prev/next navigation. The parent stays in control: navigation only emits the
    // target row.
    sections: SectionRow[];
}>();

const emit = defineEmits<{
    close: [];
    navigate: [section: SectionRow];
    handleAlarm: [section: SectionRow];
}>();

function onNavigate(target: SectionRow): void {
    emit('navigate', target);
}

function onHandleAlarm(target: SectionRow): void {
    emit('handleAlarm', target);
}

// On-demand weight/growth fetch: enabled only while the modal is open (section != null). The
// `hourly` ref is folded into the query key, so flipping the granularity toggle to/from "hour"
// re-triggers the fetch with fresh (hourly-granularity) data.
const sectionId = computed(() => props.section?.id ?? null);

// Now on the Directory's own wall clock, which is what both charts' right-hand edge is measured from.
// Off the live-KPI row rather than the browser: these buckets are stated on the farm's clock with no
// offset, so a locally computed edge sits a whole UTC offset away from them (see directoryLocalNow).
const directoryLocalNow = computed(() => props.section?.directoryLocalNow ?? null);

// Explicit granularity toggle: raw daily points, or raw hourly points fetched fresh from the
// backend. Both are drawn exactly as they arrive - no client-side bucketing - so the toggle only
// decides which resolution is fetched, via the `hourly` query param.
type WeightGranularity = 'day' | 'hour';
const { granularityOptions, granularity, resetGranularity } = useChartGranularity<WeightGranularity>(['hour', 'day'], sectionId, 'day');
const hourly = computed(() => granularity.value === 'hour');

// How many decimals each metric is written with in this modal's tooltip, from the Productivity Chart's own
// registry so a figure is not written one way on one surface and another way on the other. Not the locale's
// bare `decimal` format, which allows up to two: a Growth Index printed as "86,03" claims a hundredth of an
// index point from a derivation that has no such thing.
const tooltipDecimals = computed(() => ({
    weight: metricDecimals('weight', granularity.value),
    dailyGain: metricDecimals('dailyGain', granularity.value),
    growthIndex: metricDecimals('growthIndex', granularity.value),
}));

// Right edge is always the present moment, not the last datapoint. The granularity toggle decides
// which bucket resolution that edge must land on.
const axisMaxNow = computed(() => (hourly.value ? directoryLocalHourStart(directoryLocalNow.value) : directoryLocalDayStart(directoryLocalNow.value)));

const { data: weightData, isLoading: isSeriesLoading } = useWeightSeries(sectionId, hourly);

// The Section-wide aggregate, and the cameras that fed it.
const aggregateSeries = computed(() => weightData.value?.aggregate ?? []);
const cameras = computed(() => weightData.value?.cameras ?? []);

// A Section measured by one camera draws that one curve and nothing else: its aggregate IS that
// camera, so a second identical line and a second legend entry would put a legend on the commonest
// case in production and say nothing with it. Every per-camera decision below hangs off this.
const multiCamera = computed(() => drawsCameraSeries(cameras.value));

// A chart is worth drawing as soon as anything reported. Checked across the cameras too, not the
// aggregate alone: the aggregate carries a figure only for a bucket every camera reported, so a
// Section whose cameras covered different buckets can hold real camera curves under an aggregate
// that is almost all gaps.
const hasChartData = computed(() => aggregateSeries.value.length > 0 || cameras.value.some((camera) => camera.buckets.length > 0));

// The API returns date and hour as separate fields; charting needs one x-axis instant, so this is the one
// place that combines them. Both branches parse as UTC (explicit "Z" for the hourly form, a bare date
// string being UTC per spec), matching the sibling Water/Mortality modals - otherwise toggling
// granularity reinterprets the same reading in two zones for non-UTC users.
function bucketTimestamp(point: { date: string; hour: string | null }): number {
    return Date.parse(point.hour ? `${point.date}T${point.hour}Z` : point.date);
}

// "No active Production Group" and "sensor present but no readings yet" both arrive from the backend as an
// empty series (see GetWeightSeriesAsync) - `section.day` (null only when there's no active Study
// Period) is what distinguishes them for the empty-state message.
const chartEmptyMessage = computed(() => {
    if (!props.section) return '';
    return props.section.day === null ? i18n.global.t('views.monitoring.activeGroups.texts.noProductionGroup') : i18n.global.t('views.monitoring.activeGroups.texts.noData');
});

// Non-physical derived points (see plausibleRange.ts), counted so each chart can say how many of its
// points it blanked rather than letting the gap read as missing data. Across every curve the chart draws,
// cameras included: a camera's Daily Gain is the same day-over-day derivation on the same shared y-axis.
// The count follows the data, not the legend, so toggling a series off does not change it.
function countImplausibleAcrossSeries(value: (point: WeightSeriesDay) => number | null, range: PlausibleRange): number {
    // A single-camera Section counts its aggregate alone: no camera curve is drawn, so counting one
    // would report the same blanked point twice.
    const drawn = multiCamera.value ? [aggregateSeries.value, ...cameras.value.map((camera) => camera.buckets)] : [aggregateSeries.value];
    return drawn.reduce((total, points) => total + countImplausible(points, value, range), 0);
}

const implausibleGrowthIndexCount = computed(() => countImplausibleAcrossSeries((point) => point.growth_index, GROWTH_INDEX_RANGE));
const implausibleDailyGainCount = computed(() => countImplausibleAcrossSeries((point) => point.daily_gain_g, DAILY_GAIN_G_RANGE));

const implausibleGrowthIndexWarning = computed(() => i18n.global.tc('views.monitoring.activeGroups.texts.chartImplausibleWarning', implausibleGrowthIndexCount.value));
const implausibleDailyGainWarning = computed(() => i18n.global.tc('views.monitoring.activeGroups.texts.chartImplausibleWarning', implausibleDailyGainCount.value));

// Buckets the Section states nothing for although a camera measured something there. The aggregate is
// stated only where EVERY mounted camera reported, so a bucket one camera missed leaves a gap in a Section
// line drawn over camera lines that have a value, and unexplained that gap reads as data loss. A bucket
// nobody reported in never reaches the series, so there is no gap to explain and it is not counted.
//
// Plausibility is deliberately not applied to the camera side: this line is about whether a camera
// reported, and the amber caveat beside it speaks for non-physical values.
function countWithheldBuckets(figures: ((point: WeightSeriesDay) => number | null)[]): number {
    if (!multiCamera.value) return 0;

    // Per figure, the buckets at least one camera stated it in - a bucket withheld for the weight is
    // not evidence about the Daily Gain drawn beside it.
    const measuredByACamera = figures.map((value) => {
        const buckets = new Set<number>();
        for (const camera of cameras.value) {
            for (const bucket of camera.buckets) {
                if (value(bucket) !== null) buckets.add(bucketTimestamp(bucket));
            }
        }
        return buckets;
    });

    // Counted per chart, not per figure: two charts each stating one number answers the reader's
    // actual question - are these gaps absence or arithmetic - where three would read as three
    // separate faults.
    return aggregateSeries.value.filter((point) => figures.some((value, index) => value(point) === null && measuredByACamera[index].has(bucketTimestamp(point)))).length;
}

const withheldGrowthIndexCount = computed(() => countWithheldBuckets([(point) => point.growth_index]));
const withheldCombinedCount = computed(() => countWithheldBuckets([(point) => point.weight_kg, (point) => point.daily_gain_g]));

const withheldGrowthIndexNote = computed(() => i18n.global.tc('views.monitoring.activeGroups.texts.chartWithheldNote', withheldGrowthIndexCount.value));
const withheldCombinedNote = computed(() => i18n.global.tc('views.monitoring.activeGroups.texts.chartWithheldNote', withheldCombinedCount.value));

// The two charts, named so the things that reach across them - the shared range and the curve
// picker - can address either one.
type WeightChartId = 'growthIndex' | 'combined';
const WEIGHT_CHART_IDS: WeightChartId[] = ['growthIndex', 'combined'];

// One range for both charts: the visible navigator lives on the combined chart below, and its drags
// are mirrored onto the Growth Index chart above. One strip rather than one per chart, because two
// controls over the same window can be dragged into stating two windows for the same Section. Also
// where the rendered instances are held, mirroring a drag being what needs to reach them.
const { register: registerChart, liveChart, onSetExtremes: syncChartExtremes, reset: resetChartRange } = useSyncedChartExtremes(WEIGHT_CHART_IDS);

// The three Section-wide curves, as one thing to show or hide: they are the same statement about the
// Section on two charts, so the reader who wants the cameras alone says so once.
const AGGREGATE_SERIES_IDS: readonly string[] = ['growthIndex', 'weight', 'dailyGain'];

// Every metric a camera is drawn for, taken from the camera registry rather than listed again here.
// One physical camera is one entry in the picker, so that entry has to reach its curve on both
// charts and under every metric a camera reports - hand-listed, a metric added to the registry would
// be drawn and never be pickable.
const CAMERA_METRICS_DRAWN = METRIC_KEYS.filter(isCameraMetric);

// Which chart a metric's camera curves are drawn on. Exhaustive by type, so a metric added to the
// camera registry cannot reach the charts without an answer here - and the seed below needs one,
// since the two charts open their cameras differently.
const CAMERA_METRIC_CHART: Record<CameraMetric, WeightChartId> = { growthIndex: 'growthIndex', weight: 'combined', dailyGain: 'combined' };

// Whether each curve on either chart is drawn, keyed by Highcharts series id, because the series count is
// the Section's camera count. The single statement of what is drawn: every series is declared visible in
// the chart options and applySeriesVisibility puts this map on top, so the picker cannot show a tick the
// plot disagrees with.
//
// Reactive because the picker's checkboxes read it. The chart options must never read it: ticking one
// camera has to hide two lines, not rebuild both charts and throw away the navigator's range.
const seriesVisibility = reactive(new Map<string, boolean>());

// Whether a chart's aggregate curves have a single drawable point between them anywhere in the
// window - per chart, because each one opens on its own plot and a bare plot is a fact about that
// plot. The combined chart's two aggregates are asked together, since they share one: it is bare
// only if neither weight nor Daily Gain states anything at all.
const chartAggregateIsBlank = computed<Record<WeightChartId, boolean>>(() => ({
    growthIndex: !aggregateSeries.value.some((point) => plottableValue(point.growth_index, GROWTH_INDEX_RANGE) !== null),
    combined: !aggregateSeries.value.some((point) => point.weight_kg !== null || plottableValue(point.daily_gain_g, DAILY_GAIN_G_RANGE) !== null),
}));

// The state both charts open in, stated for every id there is rather than left to a per-chart default: an
// id with no entry forces each reader to invent a fallback, and two fallbacks are two answers to disagree.
// Only ids with no answer yet, so the reader's choices survive the refetch a granularity change triggers.
//
// The aggregates are drawn and the cameras are not: the Section figure is what the modal is opened to read,
// and on a six-camera Section the cameras outnumber it eighteen to three. The exception is a plot the
// default would leave bare - a Section holding a camera that has never reported, where one silent camera
// blanks the aggregate everywhere - asked per chart, since a Section can have Growth Index nowhere and
// weight throughout.
function seedCurveVisibility(): void {
    function seed(id: string, shown: boolean): void {
        if (!seriesVisibility.has(id)) seriesVisibility.set(id, shown);
    }

    for (const id of AGGREGATE_SERIES_IDS) seed(id, true);
    cameras.value.forEach((camera, index) => {
        for (const metric of CAMERA_METRICS_DRAWN) {
            seed(cameraSeriesKey(metric, index), chartAggregateIsBlank.value[CAMERA_METRIC_CHART[metric]]);
        }
    });
}

// An ordinal belongs to the Section whose cameras it numbers, so a choice about "Camera 2" here says
// nothing about the next Section's second camera. Cleared on navigation, for the same reason the
// granularity toggle resets, then seeded afresh as that Section's own camera list arrives.
watch(sectionId, () => seriesVisibility.clear());
watch(cameras, seedCurveVisibility, { immediate: true });

// Put every choice into one chart - after a recreate, and after each tick of the picker. The only
// writer of series visibility there is. Series carrying none of our ids - the navigator's own shadow
// series - are left alone.
function applySeriesVisibility(instance: any): void {
    if (!instance?.series) return;
    for (const series of instance.series) {
        const key: unknown = series.options?.id;
        if (typeof key === 'string') {
            const shown = seriesVisibility.get(key);
            if (shown !== undefined && shown !== series.visible) series.setVisible(shown, false);
        }
    }
    instance.redraw();
}

function onChartRendered(id: WeightChartId, instance: any): void {
    registerChart(id, instance);
    applySeriesVisibility(instance);
}

// One picker entry per camera, named as the legend names it: the Sensor's own name, or its ordinal
// where it has none.
const cameraPickerEntries = computed(() =>
    cameras.value.map((camera, index) => ({
        index,
        label: cameraLabel(camera, index),
        ids: CAMERA_METRICS_DRAWN.map((metric) => cameraSeriesKey(metric, index)),
    }))
);

// Ticked when any series the entry stands for is drawn - which, every id being seeded, is the answer
// the plots are drawing. No fallback: an id with no entry is not a curve this picker ever offered.
function isCurveShown(ids: readonly string[]): boolean {
    return ids.some((id) => seriesVisibility.get(id) === true);
}

// One tick writes every series the entry stands for, then pushes the result into both live charts -
// so from the first tick on, a camera is either drawn on both charts or on neither.
function setCurveShown(ids: readonly string[], shown: boolean): void {
    for (const id of ids) seriesVisibility.set(id, shown);
    for (const id of WEIGHT_CHART_IDS) {
        const instance = liveChart(id);
        if (instance) applySeriesVisibility(instance);
    }
}

const shownCurveCount = computed(() => [AGGREGATE_SERIES_IDS, ...cameraPickerEntries.value.map((entry) => entry.ids)].filter(isCurveShown).length);
const pickedCurveTotal = computed(() => cameraPickerEntries.value.length + 1);

// A legend is a key, not a control, wherever the picker exists: two writers on one visibility state is how a
// chart ends up disagreeing with the checkbox that set it. A single-camera Section has no picker, and there
// the legend remains the only way to put a curve away.
//
// `states.inactive.opacity` overrides Highcharts' default 0.2: under a shared tooltip every curve is being
// read, so dimming the ones the cursor is not on would drop the whole plot to a fifth of its contrast.
const chartPlotOptions = computed(() => ({
    series: {
        states: { inactive: { opacity: 1 } },
        ...(multiCamera.value ? { events: { legendItemClick: () => false } } : {}),
    },
}));

// A read-only entry must not offer a pointer: the cursor is the only thing that says whether a legend
// entry can be clicked at all.
const legendItemStyle = computed(() => ({ color: colors.gray[700], fontSize: '12px', fontWeight: '500', ...(multiCamera.value ? { cursor: 'default' } : {}) }));

// The single-camera Section's legend writes down what it toggles, so a rebuild of the options puts
// the reader's choice back rather than resetting both curves to the state they opened in.
function legendMemory(key: string) {
    if (multiCamera.value) return {};
    return {
        events: {
            legendItemClick(this: any): boolean {
                seriesVisibility.set(key, !this.visible);
                return true;
            },
        },
    };
}

// Back to the view the modal opens on: the default granularity, and the full Production Group window
// the navigator was dragged out of.
function resetChartView(): void {
    resetGranularity();
    resetChartRange();
}

// One camera's curve of a single metric: a narrow dashed line in the camera's own muted hue, drawn
// behind the aggregate (see cameraSeries.ts for why hue alone never separates the two). Named for
// the camera in the legend and for both its ordinal and its name in the tooltip, and put through the
// same plausibility gate and the same axis as the aggregate it sits under.
function buildCameraSeries(metric: CameraMetric, metricLabel: string, value: (point: WeightSeriesDay) => number | null, range: PlausibleRange | null, yAxis: number, unit: string | undefined) {
    // A camera's curve is the same metric as the aggregate it sits under, so it is written to the
    // same precision - keyed off the metric rather than passed in, which is one fewer argument to get
    // out of step with the series it belongs to.
    const decimals = tooltipDecimals.value[metric];
    return cameras.value.map((camera, index) => {
        const key = cameraSeriesKey(metric, index);
        return {
            type: 'line' as const,
            id: key,
            name: cameraSeriesName(metricLabel, camera, index),
            data: isolatedPointMarkers(camera.buckets.map((point) => [bucketTimestamp(point), range === null ? value(point) : plottableValue(value(point), range)])),
            yAxis,
            custom: { unit, decimals, tooltipLabel: cameraLabel(camera, index) },
            // Declared drawn, like every other series on either chart: what is actually drawn is
            // seriesVisibility, applied the moment the chart is ready. An options value tracking
            // that map would rebuild the chart on every tick of the picker, and a default stated
            // here would be a second answer for the picker to disagree with.
            visible: true,
            // 'band': this modal draws every camera together and offers no way to single one out,
            // so no camera here is ever the one the reader asked to see on its own.
            ...cameraSeriesStyle(index, metric, 'band'),
        };
    });
}

// The legend the camera curves bring with them. Bounded rather than left to wrap: at six cameras the
// combined chart carries fourteen entries, and a legend allowed to grow takes its rows out of the
// plot it is describing. Capped at three rows, past which Highcharts paginates it, and the chart is
// given that much more height so the cap costs the plot nothing.
const CAMERA_LEGEND_MAX_HEIGHT = 66;
const cameraLegendAllowance = computed(() => (multiCamera.value ? CAMERA_LEGEND_MAX_HEIGHT + 8 : 0));

const weightLabel = computed(() => i18n.global.t('views.monitoring.activeGroups.modals.weightDetail.weightCard') as string);
const dailyGainLabel = computed(() => i18n.global.t('views.monitoring.activeGroups.modals.weightDetail.dailyGainCard') as string);
const growthIndexLabel = computed(() => i18n.global.t('views.monitoring.activeGroups.modals.weightDetail.growthIndexCard') as string);

// A series' own tooltip label where it carries one (the ordinal-and-name form a camera needs), its
// legend name otherwise.
function tooltipSeriesLabel(series: any): string {
    return series?.userOptions?.custom?.tooltipLabel ?? series?.name ?? '';
}

// One tooltip for both charts: shared, so a hover states every curve drawn at that instant rather than
// whichever the cursor landed nearest. On a multi-camera Section that IS the comparison - the Section
// figure against each camera at one bucket - and one hover at a time is not a comparison at all.
//
// Units come from each series rather than the chart: the combined chart's two axes carry two, and the
// Growth Index carries none.
// No chart `time` is handed to the header formatter, which is what makes it omit a week number -
// the one it would append is the ISO calendar week, a different number for the same instant than
// the Production Group week the axis itself counts in (see periodOrdinalSuffix below, appended
// here instead). Both are "week N"; only one of them is the axis's own.
function sharedTooltipFormatter(this: any): string {
    const points: any[] = this.points ?? [];
    const xAxis = points[0]?.series?.xAxis;
    const ordinalSuffix = periodOrdinalSuffix(this.x, xAxis ? xAxis.max - xAxis.min : NaN);
    const header = `<span style="font-size: 10px">${formatActiveGroupsChartTooltipHeader(this.x, undefined, granularity.value === 'hour')}${ordinalSuffix}</span>`;
    return points.reduce((acc: string, point: any) => {
        if (!point.series) return acc;
        const unit: string | undefined = point.series.userOptions?.custom?.unit;
        // Rounded to its metric's own precision before formatting, the idiom the Productivity Chart's
        // productivityMetricValue already uses. A series without a declared precision would be a new
        // curve nobody gave one to, so it falls back to the locale's default rather than to zero.
        const decimals: number | undefined = point.series.userOptions?.custom?.decimals;
        const factor = 10 ** (decimals ?? 2);
        const value = i18n.global.n(Math.round(point.y * factor) / factor, 'decimal');
        return `${acc}<br/><span style="color:${point.color}">●</span> ${tooltipSeriesLabel(point.series)}: <b>${unit ? `${value} ${unit}` : value}</b>`;
    }, header);
}

// Stock charts default tooltip.split to true, which draws one box per axis rather than the single
// shared box the formatter above builds.
const sharedTooltip = { shared: true, split: false, formatter: sharedTooltipFormatter };

// The vertical rule under the cursor, saying which instant the shared tooltip is describing. Solid where the
// grid is dashed so it is not mistaken for a gridline, in the colour the Productivity Chart uses.
//
// `snap: false` is what makes it render at all: snapped, the rule needs a hovered point resolved against
// this axis, and hideCrosshair runs on every axis on every pointer move, so it would reach the DOM on no
// pointer position at all. The cost is at day granularity, where it can sit inside the bucket the tooltip
// lists rather than on its centre.
const cursorCrosshair = { width: 1, color: CHART_CHROME_COLORS.crosshair, dashStyle: 'Solid', snap: false };

// The units both charts write, read once here rather than at each of the six places that state one.
const weightUnit = computed(() => i18n.global.t('views.monitoring.activeGroups.texts.weightUnit') as string);
const dailyGainUnit = computed(() => i18n.global.t('views.monitoring.activeGroups.texts.dailyGainUnit') as string);
// The axis-only abbreviation of dailyGainUnit ("g/d" rather than "g/dag"/"g/day"/...) - the
// scientific-literature shorthand for average daily gain, not a translation shortcut, so it reads
// as correct rather than merely compressed. Confined to the combined chart's axis label below:
// the readout row and tooltip keep the full unit, where there is room for it and an abbreviation
// would only cost clarity for no space actually gained.
const dailyGainAxisUnit = computed(() => i18n.global.t('views.monitoring.activeGroups.texts.dailyGainUnitShort') as string);

// What the x axis counts from: the active Production Group's own start, so a tick reads "week 3 of
// this batch" rather than an ISO week number that renumbers on a Monday the pigs know nothing about.
// Taken from the server's own start date rather than derived backward from `day`, which drifts near
// a Directory-local day boundary. Null on a Section with no active Production Group.
//
// Frame: read through `productivityInstant`'s rule rather than a bare `Date.parse`, which puts the
// anchor on the Directory's wall clock stamped as UTC - the same frame the bucket instants this axis
// plots are already on, and the frame the day numbering must be in to match the server's own. The
// rule is what makes that hold for any of the shapes this field can arrive in: a bare date parses as
// UTC, and a date-time carrying no offset would otherwise be read as the *viewer's* local time and
// silently shift the anchor by their offset.
const productionGroupStartMs = computed(() => {
    const start = props.section?.productionGroupStart;
    return start ? productivityInstant(start) : null;
});

// The rung a tick is written at, chosen by how much of the period is currently in view: the whole
// run reads in weeks, a fortnight or less in days, and two days or less in time of day - the rung
// that carries the information once the hourly toggle is on. Day 1-7 is week 1 (periodPosition.ts).
const WEEK_LABEL_SPAN_MS = 14 * DAY_MS;
const DAY_LABEL_SPAN_MS = 2 * DAY_MS;

function periodAxisLabel(this: { value: number | string; axis: { min: number; max: number } }): string {
    const timestamp = Number(this.value);
    const startMs = productionGroupStartMs.value;
    // Nothing to count from: a Section between batches still has an axis, so it falls back to the
    // calendar date rather than to "week 0".
    if (startMs === null) return productivityDate(timestamp, 'middle');

    const spanMs = this.axis.max - this.axis.min;
    if (!Number.isFinite(spanMs) || spanMs > WEEK_LABEL_SPAN_MS) {
        return i18n.global.t('views.monitoring.activeGroups.texts.weekWithNumber', { week: weekInPeriod(startMs, timestamp) }) as string;
    }
    if (spanMs > DAY_LABEL_SPAN_MS) {
        return i18n.global.t('views.monitoring.activeGroups.texts.dayWithNumber', { day: dayInPeriod(startMs, timestamp) }) as string;
    }
    return productivityDate(timestamp, 'clock');
}

// The bracketed suffix sharedTooltipFormatter appends to its date header - the same week/day rung
// periodAxisLabel is currently writing the x-axis in, so a hover states "which tick is this" as
// well as "which date is this" without contradicting the axis. Blank at the clock rung (a bare
// time of day, same as periodAxisLabel itself) and once there is no Production Group to count
// from - the header's date already covers both of those on its own.
function periodOrdinalSuffix(timestamp: number, spanMs: number): string {
    const startMs = productionGroupStartMs.value;
    if (startMs === null) return '';

    if (!Number.isFinite(spanMs) || spanMs > WEEK_LABEL_SPAN_MS) {
        const week = i18n.global.t('views.monitoring.activeGroups.texts.weekWithNumber', { week: weekInPeriod(startMs, timestamp) }) as string;
        return ` (${week})`;
    }
    if (spanMs > DAY_LABEL_SPAN_MS) {
        const day = i18n.global.t('views.monitoring.activeGroups.texts.dayWithNumber', { day: dayInPeriod(startMs, timestamp) }) as string;
        return ` (${day})`;
    }
    return '';
}

// Where the ticks themselves land at the week/day rungs - the same two rungs periodAxisLabel writes a
// number for. Left to Highcharts' own auto placement, ticks are spaced from a calendar boundary with
// no relation to when the batch started, and the first one it draws can already be a week or more
// past the start: the reader never sees a "week 1"/"day 1" tick at all, only whatever it lands on
// next. Anchoring the grid at the Production Group's own start instead, via the same helper
// periodAxisLabel numbers ticks with, is what guarantees that first rung its own tick.
//
// Not applied at the clock rung: a bare time of day carries no ordinal to protect, and Highcharts'
// own hourly placement serves it as well as this would.
//
// Undefined, not an empty array, is what tells Highcharts to fall back to its own placement here -
// an empty array would draw an axis with no ticks at all rather than leaving it alone.
//
// The anchored grid above has no judgement of its own about how many of its ticks actually fit:
// asked for fourteen weekly ticks across a thirty-week span squeezed into a modal-width chart, it
// draws all fourteen regardless, and they render as one unreadable smear of overlapping labels.
// Highcharts' own auto placement never has this problem - it picks its tick count from the axis'
// pixel width in the first place - so `thinTickPositions` gives the anchored grid the same
// judgement: keep as many of its ticks as the axis can actually hold at a legible spacing, but
// always keep the very first and the very last, the two ends a reader orients the axis by and the
// two an even stride is most likely to drop.
const MIN_TICK_LABEL_PX = 60;

function thinTickPositions(positions: number[], axisLengthPx: number): number[] {
    if (positions.length <= 2 || !Number.isFinite(axisLengthPx) || axisLengthPx <= 0) return positions;

    const maxLabels = Math.max(2, Math.floor(axisLengthPx / MIN_TICK_LABEL_PX));
    if (positions.length <= maxLabels) return positions;

    const stride = (positions.length - 1) / (maxLabels - 1);
    const kept: number[] = [];
    for (let i = 0; i < maxLabels; i++) {
        kept.push(positions[Math.round(i * stride)]);
    }
    // Rounding two neighbouring steps to the same source index is possible once maxLabels is
    // small - de-duplicated rather than drawing one tick twice.
    return [...new Set(kept)];
}

function periodTickPositioner(this: { len: number }, min: number, max: number): number[] | undefined {
    const startMs = productionGroupStartMs.value;
    if (startMs === null || !Number.isFinite(min) || !Number.isFinite(max)) return undefined;

    const spanMs = max - min;
    let positions: number[];
    if (spanMs > WEEK_LABEL_SPAN_MS) positions = periodTickPositions(startMs, WEEK_MS, min, max);
    else if (spanMs > DAY_LABEL_SPAN_MS) positions = periodTickPositions(startMs, DAY_MS, min, max);
    else return undefined;

    return thinTickPositions(positions, this.len);
}

// Only the wording is ours: the axis stays a datetime axis, so Highcharts still places the ticks and
// the navigator and the two charts' shared extremes go on being measured in real time.
//
// `overflow: 'allow'` turns off Highcharts' own default edge-label justification (nudging the
// first/last tick label inward so it doesn't hang off the plot edge) - that justification is what
// was breaking the very first and last labels: each grew a second, truncated copy of itself
// alongside the whole one (`Uge 1Ug…`, `Uge 14Uge…`, both permanently visible) rather than
// actually shifting. Safe to leave off only because both charts' fixed margins (CHART_MARGIN_LEFT/
// RIGHT below) reserve real gutter on both sides for a first/last label to bleed into - without
// that gutter this is what clipped "Uge 1" down to "e 1" at the plot's bare left edge instead.
const periodAxisLabels = { formatter: periodAxisLabel, rotation: 0, overflow: 'allow' as const };

// Fixed, identical on both charts, rather than left to each chart's own auto-reserved axis width
// (`spacingLeft`/`spacingRight` on top of it, as this used to be): the two charts stack in the
// same modal with one shared navigator strip drawn on the lower of them, and that strip only
// reads as belonging to the chart above it if the two plot areas start and end at the very same
// pixel. Two independently auto-sized gutters drift apart the moment either chart's own labels
// change width - which is exactly how they drifted apart before this, since only the lower chart
// has a left axis at all. A fixed pair of gutters costs both charts some unused whitespace where
// their own content is narrower than the widest the other chart ever draws, in exchange for that
// alignment holding regardless of what either axis' labels say.
//
// Sized to the widest label either chart's axes can ever draw. Left: the weight axis' own figures
// only ("999,9 kg" and change - the axis carries no camera curves wider than the aggregate).
// Right: daily gain's, the widest of any axis here - a restock-derived reading reads negative and
// four digits ("-1600 g/d"; see plausibleRange.ts) - with Growth Index's bare 0-100 comfortably
// inside the same gutter.
const CHART_MARGIN_LEFT = 52;
const CHART_MARGIN_RIGHT = 64;

// App-wide chart conventions (localized axis, fonts, series palette) come from the shared wrapper;
// the custom-tooltip flag skips its per-point tooltip so the shared one below can format values.
// Stock chart (no rangeSelector/scrollbar, and no navigator of its own) — same skeleton as the
// mortality/water tally chart, but simpler: one y-axis (no split panes needed).
const growthIndexDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

const growthIndexChartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, growthIndexDefaults.value);
    delete base.yAxis;

    return merge(base, {
        chart: {
            height: 220 + cameraLegendAllowance.value,
            // This chart's one axis is on the right (see below) and never draws anything in the
            // left margin - reserved anyway, and to the combined chart's own left width, not to
            // zero: see CHART_MARGIN_LEFT/RIGHT above for why the two charts share fixed gutters.
            marginLeft: CHART_MARGIN_LEFT,
            marginRight: CHART_MARGIN_RIGHT,
            backgroundColor: 'transparent',
            zoomType: null,
        },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        // No navigator of its own: the range belongs to the pair of charts, and the combined chart
        // below carries the one strip that sets it. This axis follows those drags instead.
        navigator: { enabled: false },
        // A single-camera Section draws one curve, so a legend would be a caption on a chart that
        // already has a title. It appears exactly when there is more than one curve to tell apart.
        legend: { enabled: multiCamera.value, maxHeight: CAMERA_LEGEND_MAX_HEIGHT, itemStyle: legendItemStyle.value },
        plotOptions: chartPlotOptions.value,
        xAxis: {
            // No SP-start-based range: the whole series is the current Production Group, so it always renders
            // in full — there's nothing before Production Group start to mark a boundary against.
            min: null,
            // The present moment, not the last datapoint - a Sensor that stopped reporting
            // days ago must render as empty space up to the right edge, not as a shortened axis.
            max: axisMaxNow.value,
            tickInterval: null,
            tickPositioner: periodTickPositioner,
            labels: periodAxisLabels,
            crosshair: cursorCrosshair,
            events: { setExtremes: (event: any) => syncChartExtremes(event, 'growthIndex') },
        },
        yAxis: {
            // Hard floor, not softMin: a Growth Index below 0 is not a value the axis should ever
            // make room for. Without it one restock-derived point (-1334 on section 238) stretched
            // the axis to -2000 and squashed the real 60-120 signal into a sliver. Such points are
            // blanked out of the series below, so this is belt-and-braces for anything inside the
            // plausible band but still extreme.
            min: 0,
            // Guarantees the 100 line - and its label - a clear margin above the plotted data
            // instead of being squashed against the top edge when the index sits in the 80s/90s.
            softMax: 100,
            maxPadding: 0.08,
            // Stated, not left to whatever this chart type defaults a lone yAxis to: the axis
            // line itself draws on the right either way, but Highcharts only auto-flips its
            // *label* alignment (from right-aligned, growing left into the plot, to left-aligned,
            // growing right into the reserved gutter) off a `opposite` it can see stated - left
            // implicit, the axis rendered on the right in name only, its numbers still growing
            // leftward off the plot's own right edge and drawing straight through the data
            // (confirmed via each label's own SVG `text-anchor`: "end" - the left-axis default -
            // where the combined chart's explicitly-opposite axis below reads "start").
            opposite: true,
            title: { text: '' },
            labels: { align: 'left', style: { fontSize: '0.75rem' } },
            // 100 is the index's neutral reference: above it the section is ahead of its growth
            // target, below it behind - draw it in so the chart reads at a glance without a tooltip.
            // Amber, not the muted gridline gray, so it reads as a deliberate marker, not decoration.
            plotLines: [
                {
                    value: 100,
                    color: '#F59E0B',
                    dashStyle: 'ShortDash',
                    width: 2,
                    zIndex: 5,
                    label: {
                        text: i18n.global.t('views.monitoring.activeGroups.modals.weightDetail.indexReferenceLabel') as string,
                        // verticalAlign 'top' (the plotLine label default) draws the label above the
                        // line - since the line sits at the very top of the plot (axis max is 100),
                        // that pushed the label above the chart's own top edge and clipped it.
                        // 'bottom' draws it below the line instead, safely inside the plot area.
                        align: 'left',
                        verticalAlign: 'bottom',
                        x: 4,
                        y: -2,
                        style: { fontSize: '10px', fontWeight: '600', color: '#B45309' },
                    },
                },
            ],
        },
        tooltip: sharedTooltip,
        series: [
            {
                type: 'line',
                id: 'growthIndex',
                name: growthIndexLabel.value,
                // Stated, never left to the shared palette's creation order: Highcharts advances its
                // colour counter for every series including the cameras that carry their own colour,
                // so an inherited colour here would depend on how many cameras the Section has. The
                // Growth Index is this green wherever it is plotted, Productivity Chart included.
                color: CHART_SERIES_COLORS.growthIndex,
                // Non-physical points become null → Highcharts draws a gap, and the caveat above the chart
                // says how many; never clamped to a fake in-band number (plausibleRange.ts). The
                // aggregate's own gaps are the buckets not every camera reported. Isolated points carry
                // their own marker, without which such a curve draws no line at all - see
                // isolatedPointMarkers.
                data: isolatedPointMarkers(aggregateSeries.value.map((point) => [bucketTimestamp(point), plottableValue(point.growth_index, GROWTH_INDEX_RANGE)])),
                marker: { enabled: false },
                custom: { decimals: tooltipDecimals.value.growthIndex },
                ...aggregateEmphasis(multiCamera.value),
            },
            // Present in the legend but not, on opening, drawn - seedCurveVisibility owns that
            // choice for both charts and states there why the cameras start hidden.
            ...(multiCamera.value ? buildCameraSeries('growthIndex', growthIndexLabel.value, (point) => point.growth_index, GROWTH_INDEX_RANGE, 0, undefined) : []),
        ],
    });
});

// Same skeleton, two series on two y-axes overlaid on the same plot area (not split panes): weight
// on the primary (left) axis, daily gain on the secondary (right, opposite) axis.
const combinedDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

const combinedChartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, combinedDefaults.value);
    delete base.yAxis;

    return merge(base, {
        chart: {
            height: 260 + cameraLegendAllowance.value,
            // See growthIndexChartOptions above (CHART_MARGIN_LEFT/RIGHT): fixed, and the same
            // pair as that chart's, so the two plot areas - and the navigator this chart alone
            // carries - line up under Growth Index rather than merely resembling its width.
            marginLeft: CHART_MARGIN_LEFT,
            marginRight: CHART_MARGIN_RIGHT,
            backgroundColor: 'transparent',
            zoomType: null,
        },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        // The one navigator in the modal. On the lower of the two charts because a range strip reads
        // as belonging to whatever sits above it, and here what sits above it is both charts.
        navigator: { enabled: true, height: 30, margin: 8 },
        // maxHeight only where there are camera entries to bound - a two-series legend has never
        // needed pagination and must keep rendering exactly as it does today.
        legend: { enabled: true, ...(multiCamera.value ? { maxHeight: CAMERA_LEGEND_MAX_HEIGHT } : {}), itemStyle: legendItemStyle.value },
        plotOptions: chartPlotOptions.value,
        xAxis: {
            min: null,
            // The present moment, not the last datapoint - see growthIndexChartOptions above.
            max: axisMaxNow.value,
            tickInterval: null,
            tickPositioner: periodTickPositioner,
            labels: periodAxisLabels,
            crosshair: cursorCrosshair,
            events: { setExtremes: (event: any) => syncChartExtremes(event, 'combined') },
        },
        yAxis: [
            {
                softMin: 0,
                // Stated rather than left to default: Stock charts default a bare yAxis to
                // `opposite: true` (the price-axis-on-the-right convention), which put this axis on
                // the right on top of the daily-gain axis below instead of on the left beside it -
                // two axes stacked at the same edge reads as the one axis this chart was missing.
                opposite: false,
                gridLineDashStyle: 'Dash',
                // No axis title: a rotated title breaks rendering in this compact chart. The unit
                // rides on the tick labels instead, and each axis' labels are drawn in the colour of
                // the series they measure - with neither axis titled, that colour is the only thing
                // saying which of the two curves an axis belongs to.
                title: { text: '' },
                // A function, not the `format` template string the rest of this file's other axes
                // use: Highcharts sizes an axis' reserved width from a numeric guess at what a tick
                // will read, not from evaluating `format` - accurate for a bare number, but blind to
                // a template's own literal text, so the six characters of " kg"/" g/dag" this axis
                // appends were never counted and the unit was drawn past the edge Highcharts had
                // reserved. A `formatter` is evaluated for real, so its actual output sizes the axis.
                labels: {
                    formatter(this: { value: number | string }): string {
                        return `${this.value} ${weightUnit.value}`;
                    },
                    style: { fontSize: '0.75rem', color: CHART_SERIES_COLORS.weight },
                },
            },
            {
                softMin: 0,
                opposite: true,
                gridLineDashStyle: 'Dash',
                title: { text: '' },
                // See the weight axis above for why this is a formatter rather than a format string.
                // Abbreviated unit (dailyGainAxisUnit, not dailyGainUnit) - see its own comment above.
                labels: {
                    formatter(this: { value: number | string }): string {
                        return `${this.value} ${dailyGainAxisUnit.value}`;
                    },
                    style: { fontSize: '0.75rem', color: CHART_SERIES_COLORS.dailyGain },
                },
            },
        ],
        tooltip: sharedTooltip,
        // Grouped by metric, each aggregate followed by its own cameras, so the legend reads as two
        // families rather than as one list of six entries.
        series: [
            {
                type: 'line',
                id: 'weight',
                name: weightLabel.value,
                // See the Growth Index series above for why the colour is stated rather than taken
                // from the shared palette by series order.
                color: CHART_SERIES_COLORS.weight,
                data: isolatedPointMarkers(aggregateSeries.value.map((point) => [bucketTimestamp(point), point.weight_kg])),
                yAxis: 0,
                marker: { enabled: false },
                custom: { unit: weightUnit.value, decimals: tooltipDecimals.value.weight },
                // Fixed at creation time: the picker applies whatever the reader last chose
                // straight onto the rendered series.
                visible: true,
                ...legendMemory('weight'),
                ...aggregateEmphasis(multiCamera.value),
            },
            // Present in the legend but not, on opening, drawn - see the Growth Index chart above.
            // The picker reveals them, on both charts at once and across a granularity change.
            ...(multiCamera.value ? buildCameraSeries('weight', weightLabel.value, (point) => point.weight_kg, null, 0, weightUnit.value) : []),
            {
                type: 'line',
                id: 'dailyGain',
                name: dailyGainLabel.value,
                // The series the palette's creation order reaches last: it is created after every
                // weight camera curve, so an inherited colour here would track the Section's camera
                // count. Stated, it is the same pale green the Productivity Chart draws Daily Gain in.
                color: CHART_SERIES_COLORS.dailyGain,
                // Same treatment as the Growth Index series: a restock turns the day-over-day
                // derivation into ±1300 g/day spikes, which alone dictated this axis' -1600…1600 range.
                data: isolatedPointMarkers(aggregateSeries.value.map((point) => [bucketTimestamp(point), plottableValue(point.daily_gain_g, DAILY_GAIN_G_RANGE)])),
                yAxis: 1,
                marker: { enabled: false },
                custom: { unit: dailyGainUnit.value, decimals: tooltipDecimals.value.dailyGain },
                visible: true,
                ...legendMemory('dailyGain'),
                ...aggregateEmphasis(multiCamera.value),
            },
            ...(multiCamera.value ? buildCameraSeries('dailyGain', dailyGainLabel.value, (point) => point.daily_gain_g, DAILY_GAIN_G_RANGE, 1, dailyGainUnit.value) : []),
        ],
    });
});
</script>
